import React, { useState, useEffect } from "react";
import { FeatherCheckCircle2, FeatherXCircle } from "@subframe/core";
import { validateInvoiceDocument } from "../../../invoices/data/api/validationApi";
import { validateBillDocument } from "../../../bills/data/api/validationApi";
import { ValidationResponse } from "../../../invoices/domain/entities/ValidationEntity";

interface ValidationChecksProps {
  entityType: "invoice" | "bill";
  invoiceJson?: any;
  billJson?: any;
  invoiceDocument?: any;
  billDocument?: any;
}

export const ValidationChecks: React.FC<ValidationChecksProps> = ({
  entityType,
  invoiceJson,
  billJson,
  invoiceDocument,
  billDocument,
}) => {
  const [validationData, setValidationData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const runValidation = async () => {
      // Only validate for invoices and bills
      if (entityType !== "invoice" && entityType !== "bill") {
        return;
      }

      // Check if we have the required data
      if (entityType === "invoice") {
        if (!invoiceJson || !invoiceDocument) {
          return;
        }
      } else if (entityType === "bill") {
        if (!billJson || !billDocument) {
          return;
        }
      }

      setLoading(true);
      setError(null);

      try {
        let response: ValidationResponse;

        if (entityType === "invoice") {
          response = await validateInvoiceDocument(
            invoiceJson,
            invoiceDocument,
            "normal_validation"
          );
        } else {
          response = await validateBillDocument(
            billJson,
            billDocument,
            "normal_validation"
          );
        }

        if (response.success && response.data) {
          setValidationData(response.data);
        } else {
          setError("Validation failed");
        }
      } catch (err) {
        console.error("Validation error:", err);
        setError(err instanceof Error ? err.message : "Validation error");
      } finally {
        setLoading(false);
      }
    };

    runValidation();
  }, [entityType, invoiceJson, billJson, invoiceDocument, billDocument]);

  // Don't render if not invoice or bill
  if (entityType !== "invoice" && entityType !== "bill") {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex w-full flex-col items-start gap-3">
        <span className="text-body-bold font-body-bold text-default-font">
          Validation Checks
        </span>
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-body font-body text-subtext-color">
            Running validation...
          </span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex w-full flex-col items-start gap-3">
        <span className="text-body-bold font-body-bold text-default-font">
          Validation Checks
        </span>
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-body font-body text-error-600">
            Validation error: {error}
          </span>
        </div>
      </div>
    );
  }

  // Show validation results
  if (validationData?.validation_summary?.validation_details) {
    const validationDetails =
      validationData.validation_summary.validation_details;

    return (
      <div className="flex w-full flex-col items-start gap-3">
        <span className="text-body-bold font-body-bold text-default-font">
          Validation Checks
        </span>
        <div className="flex w-full flex-col items-start gap-2">
          {validationDetails.map((validation: any, index: number) => {
            const Icon = validation.valid
              ? FeatherCheckCircle2
              : FeatherXCircle;
            const iconColor = validation.valid
              ? "text-success-600"
              : "text-error-600";

            // Format validation name: convert snake_case to Title Case
            const formattedName = validation.validation_name
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l: string) => l.toUpperCase());

            return (
              <div key={index} className="flex items-center gap-2">
                <Icon className={`text-body font-body ${iconColor}`} />
                <span className="text-body font-body text-default-font">
                  {formattedName}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // No validation data yet
  return null;
};
